import React, { useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { Button, Col, FormInstance, Modal, Row, Space } from "antd";
import useBreakpoint from "antd/es/grid/hooks/useBreakpoint";
import { toast } from "utils/toast";

import { LandlordForm } from "js/components/Landlords/LandlordForm/LandlordForm";
import LandlordListSelect from "js/components/Landlords/LandlordListSelect/LandlordListSelect";
import { LandlordListModalProps } from "js/components/Landlords/types";
import { Landlord } from "js/components/types";

import {
  useCreateLandlord,
  useUpdateLandlord,
} from "../../../../hooks/useLandlords";
import { useUpdateProperty } from "../../../../hooks/useUpdateProperty";

const LandlordListModal: React.FC<LandlordListModalProps> = ({
  openModal,
  setOpenModal,
  setLandlordData,
}) => {
  const landlordFormRef = useRef<FormInstance>(null);
  const screen = useBreakpoint();
  const { propertyId } = useParams();
  const [isEditView, setIsEditView] = useState(false);
  const [isCreateView, setIsCreateView] = useState(false);
  const [selectedLandlord, setSelectedLandlord] = useState({});

  // TanStack Query mutations
  const updatePropertyMutation = useUpdateProperty();
  const createLandlordMutation = useCreateLandlord();
  const updateLandlordMutation = useUpdateLandlord();

  // Derived loading state from all mutations
  const loading =
    updatePropertyMutation.isPending ||
    createLandlordMutation.isPending ||
    updateLandlordMutation.isPending;

  const modalTitle = "Asigna un propietario";

  const normalizeString = (value: string): string => {
    if (value === "") {
      return value;
    }
    return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  };

  const validateFields = async () => {
    await landlordFormRef.current.validateFields();
  };

  const handleAsignLandlord = () => {
    if (!propertyId) {
      toast.error("ID de inmueble inválido.");
      return;
    }

    updatePropertyMutation.mutate(
      {
        propertyData: { landlord_id: selectedLandlord?.id },
        propertyId,
      },
      {
        onSuccess: () => {
          toast.success("Se ha asignado el propietario correctamente.");
          setLandlordData(selectedLandlord);
          setOpenModal(false);
        },
        onError: () => {
          toast.error("Ha ocurrido un error.");
        },
      },
    );
  };

  const closeModal = () => {
    setOpenModal(false);
    setSelectedLandlord({});
    setIsEditView(false);
    setIsCreateView(false);
  };

  const handleSaveOrUpdateLandlord = (values: any, isLegalPerson: boolean) => {
    const sanitizeOptionalField = (fieldValue?: string | null) =>
      fieldValue === undefined || fieldValue === null
        ? ""
        : normalizeString(fieldValue);

    const landlord: Landlord = {
      name: normalizeString(values.name),
      first_surname: normalizeString(values.first_surname),
      email: normalizeString(values.email),
      phone_number: values.phone_number,
      legal_type: isLegalPerson ? 2 : 1,
      second_surname: sanitizeOptionalField(values.second_surname),
      legal_entity_name: isLegalPerson
        ? sanitizeOptionalField(values.legal_entity_name)
        : "",
    };

    if (isEditView) {
      const mergedLandlord: Landlord = {
        ...(selectedLandlord as Landlord),
        ...landlord,
      };
      updateLandlordMutation.mutate(
        {
          landlord: mergedLandlord,
          landlordId: selectedLandlord.id,
        },
        {
          onSuccess: (landlordValues) => {
            setSelectedLandlord(landlordValues);
            setIsEditView(false);
            toast.success("Propietario actualizado correctamente.");
          },
          onError: () => {
            toast.error("Error al actualizar el propietario.");
          },
        },
      );
    } else if (isCreateView) {
      createLandlordMutation.mutate(landlord, {
        onSuccess: (landlordValues) => {
          setSelectedLandlord(landlordValues);
          setIsCreateView(false);
          toast.success("Propietario creado correctamente.");
        },
        onError: () => {
          toast.error("Error al crear el propietario.");
        },
      });
    }
  };

  return (
    <>
      <Modal
        title={modalTitle}
        open={openModal}
        onCancel={() => closeModal()}
        width={1000}
        centered
        footer={null}
      >
        <Row style={{ width: "100%", marginBottom: 10, paddingRight: 25 }}>
          <Col xs={24} sm={24} md={24} lg={24}>
            <Space
              direction="horizontal"
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "end",
              }}
            >
              {Object.keys(selectedLandlord).length > 0 &&
                !isEditView &&
                !isCreateView && (
                  <Button
                    loading={loading}
                    type="primary"
                    onClick={() => {
                      if (isEditView) {
                        setIsEditView(false);
                      }
                      setIsEditView(!isEditView);
                      setIsCreateView(false);
                    }}
                  >
                    Editar Información
                  </Button>
                )}
              {!isEditView && !isCreateView && (
                <Button
                  loading={loading}
                  type="primary"
                  onClick={() => {
                    setIsCreateView(!isCreateView);
                    setIsEditView(false);
                  }}
                >
                  Registrar Propietario
                </Button>
              )}
            </Space>
          </Col>
        </Row>
        <Row style={{ width: "100%" }}>
          <Col xs={24} sm={24} md={24} lg={24}>
            {(isEditView || isCreateView) && (
              <LandlordForm
                ref={landlordFormRef}
                handleSave={handleSaveOrUpdateLandlord}
                legalTypeValue={
                  Object.keys(selectedLandlord).length > 0
                    ? selectedLandlord?.legal_type === 1
                      ? "pf"
                      : "pm"
                    : "pf"
                }
                {...(isEditView ? { selectedLandlord } : {})}
              />
            )}
            {!isEditView && !isCreateView && (
              <LandlordListSelect
                selectedLandlord={selectedLandlord}
                setSelectedLandlord={setSelectedLandlord}
              />
            )}
          </Col>
          {(isEditView || isCreateView) && (
            <Space
              style={{ width: "100%", justifyContent: "end" }}
              direction="horizontal"
            >
              <Button
                loading={loading}
                onClick={() => {
                  setIsCreateView(false);
                  setIsEditView(false);
                }}
              >
                Cancelar
              </Button>
              <Button loading={loading} type="primary" onClick={validateFields}>
                {isEditView ? "Guardar Cambios" : "Registrar"}
              </Button>
            </Space>
          )}
        </Row>
        {!isEditView && !isCreateView && (
          <Row
            justify={"end"}
            gutter={{ xs: 24, sm: 24, md: 24, lg: 24 }}
            style={{
              gap: 10,
              marginRight: 22,
              marginTop: !screen.lg ? 25 : 10,
            }}
          >
            <Button type="default" onClick={closeModal}>
              Cancelar
            </Button>
            {Object.keys(selectedLandlord).length !== 0 && (
              <Button type="primary" onClick={handleAsignLandlord}>
                Asignar
              </Button>
            )}
          </Row>
        )}
      </Modal>
    </>
  );
};

export default LandlordListModal;
